import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { NoteAdd, Search, Refresh, Add, CheckCircle, Cancel, Delete, Edit, Store } from '@mui/icons-material';
import CustomSwitch from '../../components/CustomSwitch';
import CustomModal from '../../components/CustomModal';
import CustomSnackBar from '../../components/CustomSnackBar';
import CleanningButton from '../../components/CleanningButton';
import MainText from '../../components/MainText';
import API, { PaymentCrud } from '../../services/api';
import { useDispatch } from 'react-redux';
import { addTab } from '../../tabs/tabsSlice';

// =========================
// Utils
// =========================
const formatCurrency = (v) => {
  try {
    return new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(Number(v || 0));
  } catch {
    const num = Number(v || 0);
    return `Q ${num.toFixed(2)}`;
  }
};

const formatDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    const day = String(dt.getDate()).padStart(2, '0');
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const year = dt.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return String(d).slice(0, 10);
  }
};

// =========================
// Cell helpers (centrado X/Y)
// =========================
const CellCenter = ({ children }) => (
  <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}>
    {children}
  </Box>
);

const CellRight = ({ children }) => (
  <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
    {children}
  </Box>
);

// =========================
// Form (modal)
// =========================
function CreditNoteForm({ initial, onSubmit, suppliers }) {
  const [form, setForm] = useState({
    supplier: initial?.supplier || '',
    number: initial?.number || '',
    amount: initial?.amount ?? '',
    remaining_amount: initial?.remaining_amount ?? initial?.amount ?? '',
    date: initial?.date || '',
    reason: initial?.reason || '',
    for_adjustments_only: initial?.for_adjustments_only ?? false,
    is_active: initial?.is_active ?? true,
  });

  useEffect(() => {
    setForm({
      supplier: initial?.supplier || '',
      number: initial?.number || '',
      amount: initial?.amount ?? '',
      remaining_amount: initial?.remaining_amount ?? initial?.amount ?? '',
      date: initial?.date || '',
      reason: initial?.reason || '',
      for_adjustments_only: initial?.for_adjustments_only ?? false,
      is_active: initial?.is_active ?? true,
    });
  }, [initial]);

  const handleChange = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const handleSave = () => {
    const payload = {
      supplier: form.supplier || null,
      number: String(form.number || '').trim(),
      amount: Number(form.amount || 0),
      remaining_amount: Number((form.remaining_amount !== '' ? form.remaining_amount : form.amount) || 0),
      date: form.date || null,
      reason: String(form.reason || '').trim(),
      for_adjustments_only: Boolean(form.for_adjustments_only),
      is_active: Boolean(form.is_active),
    };
    onSubmit(payload);
  };

  const selectedSupplier = suppliers.find((s) => s.id === form.supplier) || null;

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <Autocomplete
        size="small"
        options={suppliers}
        getOptionLabel={(opt) => opt.companyName || opt.name || opt.legalName || 'Proveedor'}
        value={selectedSupplier}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        onChange={(_, val) => handleChange('supplier', val ? val.id : '')}
        renderInput={(params) => <TextField {...params} label="Proveedor" placeholder="Selecciona proveedor" />}
      />

      <TextField
        size="small"
        label="Numero de nota"
        value={form.number}
        onChange={(e) => handleChange('number', e.target.value)}
      />

      <TextField
        size="small"
        label="Fecha"
        type="date"
        value={form.date || ''}
        onChange={(e) => handleChange('date', e.target.value)}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        size="small"
        label="Monto"
        type="number"
        inputProps={{ step: '0.01' }}
        value={form.amount}
        onChange={(e) => handleChange('amount', e.target.value)}
      />

      <TextField
        size="small"
        label="Saldo pendiente"
        type="number"
        inputProps={{ step: '0.01' }}
        value={form.remaining_amount}
        onChange={(e) => handleChange('remaining_amount', e.target.value)}
      />

      <TextField
        size="small"
        label="Razon"
        value={form.reason}
        onChange={(e) => handleChange('reason', e.target.value)}
        multiline
        minRows={2}
      />

      <CustomSwitch
        name="for_adjustments_only"
        label={form.for_adjustments_only ? 'Solo ajustes' : 'Aplica a facturas'}
        initialValue={form.for_adjustments_only}
        onChange={(_, v) => handleChange('for_adjustments_only', v)}
      />
      <CustomSwitch
        name="is_active"
        label={form.is_active ? 'Activa' : 'Inactiva'}
        initialValue={form.is_active}
        onChange={(_, v) => handleChange('is_active', v)}
      />

      <Box sx={{ textAlign: 'right' }}>
        <Button variant="contained" onClick={handleSave}>
          Guardar
        </Button>
      </Box>
    </Stack>
  );
}

// =========================
// List
// =========================
export default function CreditNoteList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({ search: '', active: true });
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSev, setSnackSev] = useState('info');

  const [suppliers, setSuppliers] = useState([]);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(addTab({ label: 'Notas de credito', path: '/payment/credit-notes' }));
  }, [dispatch]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search || ''), 400);
    return () => clearTimeout(t);
  }, [filters.search]);

  // suppliers minimal
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await API.get('suppliers/minimal/');
        setSuppliers(Array.isArray(res.data) ? res.data : []);
      } catch {
        setSuppliers([]);
      }
    };
    fetchSuppliers();
  }, []);

  const supplierMap = useMemo(() => {
    const m = {};
    (suppliers || []).forEach((s) => {
      m[s.id] = s.companyName || s.name || s.legalName || '';
    });
    return m;
  }, [suppliers]);

  const handleFilter = (name, value) => setFilters((v) => ({ ...v, [name]: value }));
  const clearAll = () => setFilters({ search: '', active: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await PaymentCrud.creditNotes.list();
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);

      let out = data.map((c) => ({
        id: c.id,
        ...c,
        supplier_name: supplierMap[c.supplier] || c.supplier || '',
      }));

      // filtro activo/inactivo
      out = out.filter((r) => (filters.active ? r.is_active !== false : r.is_active === false));

      // filtro búsqueda
      const s = (debouncedSearch || '').toLowerCase().trim();
      if (s) {
        out = out.filter((r) =>
          String(r.number || '').toLowerCase().includes(s) ||
          String(r.supplier_name || '').toLowerCase().includes(s) ||
          String(r.reason || '').toLowerCase().includes(s)
        );
      }

      setRows(out);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.active, supplierMap]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditRow(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditRow(row); setDialogOpen(true); };

  const handleSubmit = async (payload) => {
    try {
      const body = {
        ...payload,
        supplier: payload.supplier || null,
        remaining_amount: payload.remaining_amount || payload.amount,
      };
      if (editRow) await PaymentCrud.creditNotes.update(editRow.id, body);
      else await PaymentCrud.creditNotes.create(body);

      setDialogOpen(false);
      setEditRow(null);
      load();

      setSnackMsg(editRow ? 'Nota actualizada' : 'Nota creada');
      setSnackSev('success');
      setSnackOpen(true);
    } catch {
      setSnackMsg('No se pudo guardar la nota de credito');
      setSnackSev('error');
      setSnackOpen(true);
    }
  };

  const toggleActive = async (row) => {
    try {
      await PaymentCrud.creditNotes.update(row.id, { is_active: !row.is_active });
      load();
      setSnackMsg(row.is_active ? 'Nota desactivada' : 'Nota activada');
      setSnackSev('success');
      setSnackOpen(true);
    } catch {
      // silencio como tu código original
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm('Eliminar nota de credito permanentemente?')) return;
    try {
      await PaymentCrud.creditNotes.remove(row.id);
      load();
      setSnackMsg('Nota eliminada');
      setSnackSev('success');
      setSnackOpen(true);
    } catch {
      // silencio como tu código original
    }
  };

  const columns = useMemo(() => ([
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      renderCell: (p) => (
        <CellCenter>
          <Typography variant="caption" color="text.secondary">{p.value}</Typography>
        </CellCenter>
      ),
    },
    {
      field: 'number',
      headerName: 'Numero',
      width: 160,
      renderCell: ({ value }) => (
        <CellCenter>
          <Typography sx={{ fontWeight: 800 }}>{value || '-'}</Typography>
        </CellCenter>
      ),
    },
    {
      field: 'supplier_name',
      headerName: 'Proveedor',
      flex: 1,
      minWidth: 220,
      renderCell: ({ value }) => (
        <CellCenter>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Store fontSize="small" sx={{ opacity: 0.7 }} />
            <Typography noWrap title={value} sx={{ fontWeight: 600 }}>
              {value || '-'}
            </Typography>
          </Stack>
        </CellCenter>
      ),
    },
    {
      field: 'reason',
      headerName: 'Razon',
      flex: 1,
      minWidth: 240,
      renderCell: ({ value }) => (
        <CellCenter>
          <Typography noWrap title={value}>{value || '-'}</Typography>
        </CellCenter>
      ),
    },
    {
      field: 'date',
      headerName: 'Fecha',
      width: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => (
        <CellCenter>
          <Typography>{formatDate(value) || '-'}</Typography>
        </CellCenter>
      ),
    },
    {
      field: 'amount',
      headerName: 'Monto',
      width: 150,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => (
        <CellRight>
          <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {formatCurrency(row.amount)}
          </Typography>
        </CellRight>
      ),
    },
    {
      field: 'remaining_amount',
      headerName: 'Saldo',
      width: 150,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => (
        <CellRight>
          <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(row.remaining_amount)}
          </Typography>
        </CellRight>
      ),
    },
    {
      field: 'for_adjustments_only',
      headerName: 'Solo ajustes',
      width: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => (
        <CellCenter>
          <Chip
            size="small"
            label={value ? 'Si' : 'No'}
            color={value ? 'warning' : 'default'}
            variant="outlined"
          />
        </CellCenter>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Estado',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ value }) => (
        <CellCenter>
          <Chip
            size="small"
            label={value === false ? 'Inactiva' : 'Activa'}
            color={value === false ? 'default' : 'success'}
            variant="outlined"
          />
        </CellCenter>
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 160,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => (
        <CellCenter>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => openEdit(row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>

            {filters.active ? (
              <Tooltip title={row.is_active === false ? 'Activar' : 'Desactivar'}>
                <IconButton size="small" onClick={() => toggleActive(row)}>
                  {row.is_active === false
                    ? <CheckCircle fontSize="small" color="success" />
                    : <Cancel fontSize="small" color="warning" />
                  }
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Eliminar permanentemente">
                <IconButton size="small" color="error" onClick={() => removeRow(row)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </CellCenter>
      ),
    },
  ]), [filters.active]); // mantenemos la dependencia como en tu patrón

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header estilo patrón "PaymentsList" (limpio) */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, width: '100%' }}>
        <Box sx={{ flexGrow: 1 }}>
          <MainText title="Notas de credito" subtitle="Gestion rapida de notas emitidas al proveedor." icon={NoteAdd} width="auto" />
        </Box>

        {/* Chips solo cuando aplica (menos ruido) */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!!filters.search?.trim() && (
            <Chip size="small" label={`Busqueda: "${filters.search}"`} onDelete={() => handleFilter('search', '')} />
          )}
          {filters.active === false && (
            <Chip size="small" label="Mostrando inactivas" color="warning" onDelete={() => handleFilter('active', true)} />
          )}
          {!filters.search && filters.active && (
            <Chip size="small" label="Mostrando activas" color="info" />
          )}
        </Box>

        <CleanningButton onClick={clearAll} />
      </Box>

      {/* Barra de filtros */}
      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Buscar numero, proveedor o razon..."
          value={filters.search}
          onChange={(e) => handleFilter('search', e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ width: { xs: '100%', sm: 320 } }}
        />

        <CustomSwitch
          name="active"
          label={filters.active ? 'Activas' : 'Inactivas'}
          initialValue={filters.active}
          onChange={(_, val) => handleFilter('active', val)}
        />

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refrescar">
            <IconButton onClick={load}><Refresh /></IconButton>
          </Tooltip>

          <Button
            onClick={openCreate}
            startIcon={<Add />}
            size="small"
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 1.5,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none', transform: 'translateY(-1px)' }
            }}
          >
            Crear
          </Button>
        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ height: 480, width: '100%', mt: 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableColumnFilter
          disableColumnMenu
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          rowHeight={50}
          columnHeaderHeight={50}
          sx={{
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#10173c', color: '#fff' },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#10173c !important',
              '&:hover, &:focus, &:active': { backgroundColor: '#10173c !important' }
            },
            '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator': {
              color: '#ffffff !important'
            },
            '& .MuiDataGrid-cell': { padding: '0 10px', borderBottom: '1px solid #f0f0f0' },
            '& .MuiDataGrid-row:hover': { cursor: 'pointer', backgroundColor: 'rgba(16, 23, 60, 0.08)' },
          }}
        />
      </Box>

      {/* Modal */}
      <CustomModal
        open={dialogOpen}
        setOpen={(v) => { setDialogOpen(v); if (!v) setEditRow(null); }}
        title={editRow ? 'Editar nota de credito' : 'Crear nota de credito'}
        component={CreditNoteForm}
        componentProps={{ initial: editRow, onSubmit: handleSubmit, suppliers }}
        width="560px"
      />

      {/* Snack */}
      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}
